import React from 'react';
import { Badge, Box, Card, CardBody, Heading, HStack, SimpleGrid, Spinner, Stat, StatLabel, StatNumber, Text, VStack } from '@chakra-ui/react';
import { DASHBOARD_SURFACE, DASHBOARD_TYPE, getSurfaceCardProps } from './config/dashboardTokens';
import { formatCurrency, formatNumber, formatPercentage } from './utils/formatters';
import RetailSection from './members/RetailSection';
import AviationServicesSection from './members/AviationServicesSection';
import AirportsSection from './members/AirportsSection';
import HospitalitySection from './members/HospitalitySection';
import DestinationSection from './members/DestinationSection';
import TourismDevelopmentSection from './members/TourismDevelopmentSection';
import TransactionDetailModal from './components/TransactionDetailModal';
import DashboardFilters from './components/DashboardFilters';
import ByMemberGrid from './components/ByMemberGrid';
import { useIeemDashboardData } from './hooks/useIeemDashboardData';

const MemberLogos = {
  DESTINATION: '/images/members/destination.png',
  RETAIL: '/images/members/retail.png',
  AVIATION_SERVICES: '/images/members/aviation-services.png',
  HOSPITALITY: '/images/members/hospitality.png',
  AIRPORTS: '/images/members/airports.png',
  TOURISM_DEVELOPMENT: '/images/members/tourism-development.png',
};

const DashboardIEEM = () => {
  const {
    isInj,
    userMember,
    loading,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    dashboardData,
    showDetail,
    selectedMember,
    chartLoading,
    chartData,
    topSpendersLoading,
    topSpenders,
    mostPurchasedItemsLoading,
    mostPurchasedItems,
    memberSummaryLoading,
    memberSummary,
    memberLabels,
    COLORS,
    handleDetailClick,
    handleShowCustomerDetail,
    handleShowProductDetail,
    isDetailModalOpen,
    onDetailModalClose,
    detailTitle,
    detailLoading,
    detailTransactions,
  } = useIeemDashboardData();

  const surfaceCard = getSurfaceCardProps();

  const sharedMemberSectionProps = {
    memberSummaryLoading,
    memberSummary,
    memberLabels,
    chartLoading,
    chartData,
    COLORS,
    surfaceCard,
    DASHBOARD_TYPE,
    DASHBOARD_SURFACE,
    formatNumber,
    formatCurrency,
    formatPercentage,
  };

  const renderMemberSection = () => {
    switch (selectedMember) {
      case 'RETAIL':
        return (
          <RetailSection
            {...sharedMemberSectionProps}
            topSpendersLoading={topSpendersLoading}
            topSpenders={topSpenders}
            mostPurchasedItemsLoading={mostPurchasedItemsLoading}
            mostPurchasedItems={mostPurchasedItems}
            handleShowCustomerDetail={handleShowCustomerDetail}
            handleShowProductDetail={handleShowProductDetail}
          />
        );
      case 'AVIATION_SERVICES':
        return <AviationServicesSection {...sharedMemberSectionProps} />;
      case 'AIRPORTS':
        return <AirportsSection />;
      case 'HOSPITALITY':
        return <HospitalitySection />;
      case 'DESTINATION':
        return <DestinationSection />;
      case 'TOURISM_DEVELOPMENT':
        return <TourismDevelopmentSection />;
      default:
        return <AirportsSection />;
    }
  };

  return (
    <Box bg={DASHBOARD_SURFACE.pageBg}>
      <DashboardFilters dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />

      {isInj && (
        <Card bg="rgba(255, 255, 255, 0.86)" borderRadius="18px" boxShadow="0 8px 30px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)" mb={6}>
          <CardBody>
            <Heading size="md" mb={4} color="rgba(0, 0, 0, 0.85)" letterSpacing="-0.3px">All InJourney</Heading>
            {loading ? (
              <Spinner />
            ) : (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Total Num of Trx</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(dashboardData.allInjourney.totalTrx)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Total Gross Amount</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(dashboardData.allInjourney.totalGrossAmount)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Total User</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(dashboardData.allInjourney.totalUser)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Total Disc Amount</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(dashboardData.allInjourney.totalDiscAmount)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Total Active Employee</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(dashboardData.allInjourney.totalActiveEmployee)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Total Net Amount</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(dashboardData.allInjourney.totalNetAmount)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Usage Ratio</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatPercentage(dashboardData.allInjourney.usageRatio)}</StatNumber></Stat>
                <Stat bg="white" p={4} borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.06)"><StatLabel color="gray.600" fontSize="sm">Avg Disc Rate</StatLabel><StatNumber fontSize="xl" fontWeight="700" color="rgba(0, 0, 0, 0.85)" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatPercentage(dashboardData.allInjourney.avgDiscRate)}</StatNumber></Stat>
              </SimpleGrid>
            )}
          </CardBody>
        </Card>
      )}

      <ByMemberGrid isInj={isInj} userMember={userMember} loading={loading} dashboardData={dashboardData} memberLogos={MemberLogos} formatNumber={formatNumber} formatCurrency={formatCurrency} onDetailClick={handleDetailClick} />

      {showDetail && selectedMember !== 'RETAIL' && selectedMember !== 'AVIATION_SERVICES' ? (
        <Card bg="white" borderRadius="16px" boxShadow="sm" mb={6}><CardBody><Box textAlign="center" py={12}><Text fontSize="xl" fontWeight="600" color="gray.500">Coming Soon</Text><Text fontSize="sm" color="gray.400" mt={2}>Detail untuk {selectedMember ? selectedMember.replace(/_/g, ' ') : 'member ini'} akan segera hadir</Text></Box></CardBody></Card>
      ) : null}

      {showDetail && (
        <Card bg={DASHBOARD_SURFACE.panelBg} borderRadius="18px" boxShadow="0 1px 2px rgba(15,23,42,0.05), 0 10px 28px rgba(15,23,42,0.03)" border="1px solid" borderColor={DASHBOARD_SURFACE.border} mb={6}>
          <CardBody p={{ base: 4, md: 6 }}>
            <Heading fontSize={DASHBOARD_TYPE.title} lineHeight="1.2" mb={2} color="rgba(0, 0, 0, 0.85)" letterSpacing="-0.3px" sx={{ textWrap: 'balance' }}>Detail by Member</Heading>
            <Text fontSize="sm" color="rgba(0, 0, 0, 0.56)" mb={6}>{selectedMember ? selectedMember.replace(/_/g, ' ') : 'Member'} performance overview</Text>
            <HStack mb={6} justify="space-between" align={{ base: 'flex-start', md: 'center' }} flexDirection={{ base: 'column', md: 'row' }} spacing={4}>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="rgba(0, 0, 0, 0.45)" textTransform="uppercase" letterSpacing="0.08em">Active Member</Text>
                <Text fontSize="lg" fontWeight="700" color="rgba(0, 0, 0, 0.85)">{selectedMember ? selectedMember.replace(/_/g, ' ') : '-'}</Text>
                {selectedMember === 'RETAIL' && <Badge borderRadius="full" px={2.5} py={1} bg="rgba(0,122,255,0.10)" color="#005BB3" fontWeight="600">IEEM Performance</Badge>}
              </VStack>
              <Box w="72px" h="72px" bg="gray.100" borderRadius="14px" border="1px solid" borderColor="rgba(0, 0, 0, 0.08)" display="flex" alignItems="center" justifyContent="center" overflow="hidden" flexShrink={0}>
                {MemberLogos[selectedMember] ? (
                  <img src={MemberLogos[selectedMember]} alt={selectedMember ? selectedMember.replace(/_/g, ' ') : ''} style={{ width: '100%', height: '100%', objectFit: 'contain', outline: '1px solid rgba(0, 0, 0, 0.1)' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : null}
                <Box display={MemberLogos[selectedMember] ? 'none' : 'flex'} alignItems="center" justifyContent="center" w="100%" h="100%"><Text fontSize="xs" fontWeight="700" color="gray.600">{selectedMember ? selectedMember.replace(/_/g, ' ').substring(0, 8) : ''}</Text></Box>
              </Box>
            </HStack>
            {renderMemberSection()}
          </CardBody>
        </Card>
      )}

      <TransactionDetailModal isOpen={isDetailModalOpen} onClose={onDetailModalClose} detailTitle={detailTitle} detailLoading={detailLoading} detailTransactions={detailTransactions} selectedMember={selectedMember} formatCurrency={formatCurrency} />
    </Box>
  );
};

export default DashboardIEEM;
